import {
  Disclaimer,
  Div,
  EditPenIconRed,
  H5,
  Icon,
  P,
  Pill,
  lightTheme,
  taxRateLabel,
} from '@vgrubs/components';
import { TaxRateEntity } from 'components/src/types/taxRate.types';

type Props = {
  /** The effective list - what the item will actually be published with. */
  taxRates: TaxRateEntity[];
  /** False when the list is the location's, true when it is the item's own. */
  overridden: boolean;
  /** Omitted on a POS location, where taxes are synced and read-only. */
  onEdit?: () => void;
};

/**
 * The taxes an item or option is published with, as the item form shows them.
 *
 * The note underneath is the whole point: an inherited list changes when the
 * location's rates change, an overridden one does not, and nothing else on the
 * screen says which of the two you are looking at.
 */
export const TaxRatesSection = ({ taxRates, overridden, onEdit }: Props) => (
  <div>
    <Div $flex $alignCenter $between $mb="8">
      <H5 $mb="0">Taxes</H5>
      {onEdit && (
        <Icon $pointer $size="32" onClick={onEdit}>
          <EditPenIconRed />
        </Icon>
      )}
    </Div>

    {taxRates.length > 0 ? (
      <Div $flex $wrap $gap="8px">
        {taxRates.map((taxRate) => (
          <Pill
            key={taxRate.id}
            // White on the cream modal, bordered like every other box here -
            // a grey fill reads as disabled next to the fields above.
            $background={lightTheme.white}
            $border={lightTheme.mediumGray}
            $minWidth="0"
            $padding="4px 12px"
          >
            <P $m="0" $color={lightTheme.darkGray}>
              {taxRateLabel(taxRate)}
            </P>
          </Pill>
        ))}
      </Div>
    ) : (
      <P $m="0" $left>
        No taxes
      </P>
    )}

    <Disclaimer $m="8px 0 0" $left $color={lightTheme.darkGray}>
      {overridden
        ? "Set on this item. The location's taxes do not apply to it."
        : 'Inherited from the location.'}
    </Disclaimer>
  </div>
);

/**
 * The same fact compressed to a badge, for rows with no space for the list -
 * an option inside a modifier group. Inherited options show nothing: that is
 * the normal case and the location screen already spells it out.
 */
export const TaxRatesOverriddenPill = ({
  overridden,
}: {
  overridden: boolean;
}) => {
  if (!overridden) return null;

  return (
    <Pill
      $background={lightTheme.white}
      $border={lightTheme.mediumGray}
      $minWidth="0"
      $padding="1px 10px"
    >
      <Disclaimer $m="0" $color={lightTheme.darkGray}>
        Custom taxes
      </Disclaimer>
    </Pill>
  );
};
